import { Round } from '../../modules/Round.js';
import './camera-models.js';
import { Matrix } from './sylvester.src.js';
import { DistortionCorrectionEnabled } from '../../modules/DistortionCorrectionEnabled.js';
/*global Matrix, cameraModels */
var calibratePhoto = {
  ready: function () {
    for (var key in cameraModels) {
      this[key] = cameraModels[key];
    }
  },

  /**
   * Corrects the pixel values based on the camera's distortion values
   * @param {number} pixelX Pixel value for the x-coordinate in distorted image
   * @param {number} pixelY Pixel value for the y-coordinate in distorted image
   * @param {*} stickAlign Stick alignment values (including distortion values)
   * @returns {Object} {correctedX: number, correctedY: number}
   */
  correctPixel: function (pixelX, pixelY, stickAlign) {
    if (
      typeof stickAlign.aD === 'undefined' &&
      typeof stickAlign.bD === 'undefined' &&
      typeof stickAlign.cD === 'undefined' &&
      typeof stickAlign.dD === 'undefined'
    ) {
      stickAlign.aD = 0;
      stickAlign.bD = 0;
      stickAlign.cD = 0;
      stickAlign.dD = 1;
    } else if (stickAlign.aD == 0 && stickAlign.bD == 0 && stickAlign.cD == 0 && stickAlign.dD == 0) {
      stickAlign.dD = 1;
    }
    var topBorder = stickAlign.topBorder || 0;
    var bottomBorder = stickAlign.bottomBorder || 0;
    var leftBorder = stickAlign.leftBorder || 0;
    var rightBorder = stickAlign.rightBorder || 0;
    var topChop = stickAlign.topChop || 0;
    var bottomChop = stickAlign.bottomChop || 0;
    var leftChop = stickAlign.leftChop || 0;
    var rightChop = stickAlign.rightChop || 0;
    var originalWidth = stickAlign.originalWidth || stickAlign.width;
    var currentWidth = stickAlign.currentWidth || stickAlign.width;
    var originalHeight = stickAlign.originalHeight || stickAlign.height;
    var currentHeight = stickAlign.currentHeight || stickAlign.height;

    pixelX = pixelX * ((originalWidth - leftChop - rightChop + leftBorder + rightBorder) / currentWidth);
    pixelY = pixelY * ((originalHeight - topChop - bottomChop + topBorder + bottomBorder) / currentHeight);
    var xLeg = pixelX - originalWidth / 2;
    var yLeg = pixelY - originalHeight / 2;
    var minDimension = originalWidth;
    if (originalHeight < minDimension) {
      minDimension = originalHeight;
    }
    var r0 = Math.pow(Math.pow(xLeg, 2) + Math.pow(yLeg, 2), 0.5) / (minDimension / 2);
    var rSrc = r0;
    var rDiff = Number.MAX_VALUE;
    var rCalc = 0;
    var threshold = Math.pow(10, -10);
    while (rDiff > threshold) {
      rCalc =
        stickAlign.aD * Math.pow(rSrc, 4) + stickAlign.bD * Math.pow(rSrc, 3) + stickAlign.cD * Math.pow(rSrc, 2) + stickAlign.dD * rSrc;
      rDiff = r0 - rCalc;
      rSrc = rSrc + rDiff;
    }
    const correctedX = (rSrc / r0) * xLeg + originalWidth / 2;
    const correctedY = (rSrc / r0) * yLeg + originalHeight / 2;

    return { correctedX, correctedY };
  },
  getPixel: function (inches, stickAlign) {
    if (
      typeof stickAlign.aD === 'undefined' &&
      typeof stickAlign.bD === 'undefined' &&
      typeof stickAlign.cD === 'undefined' &&
      typeof stickAlign.dD === 'undefined'
    ) {
      stickAlign.aD = 0;
      stickAlign.bD = 0;
      stickAlign.cD = 0;
      stickAlign.dD = 1;
    } else if (stickAlign.aD == 0 && stickAlign.bD == 0 && stickAlign.cD == 0 && stickAlign.dD == 0) {
      stickAlign.dD = 1;
    }
    var topBorder = stickAlign.topBorder || 0;
    var bottomBorder = stickAlign.bottomBorder || 0;
    var leftBorder = stickAlign.leftBorder || 0;
    var rightBorder = stickAlign.rightBorder || 0;
    var topChop = stickAlign.topChop || 0;
    var bottomChop = stickAlign.bottomChop || 0;
    var leftChop = stickAlign.leftChop || 0;
    var rightChop = stickAlign.rightChop || 0;
    var originalWidth = stickAlign.originalWidth || stickAlign.width;
    var currentWidth = stickAlign.currentWidth || stickAlign.width;
    var originalHeight = stickAlign.originalHeight || stickAlign.height;
    var currentHeight = stickAlign.currentHeight || stickAlign.height;

    var y = (inches / 12 - stickAlign.B) / (stickAlign.A - (inches / 12) * stickAlign.C) - topChop + topBorder;
    var quotientTop = stickAlign.stickTopX - stickAlign.stickBottomX;
    var quotientBottom = stickAlign.stickTopY - stickAlign.stickBottomY;
    var x = stickAlign.stickBottomX + (y - stickAlign.stickBottomY) * (quotientTop / quotientBottom) - leftChop - leftBorder;
    var xLeg = x - originalWidth / 2;
    var yLeg = y - originalHeight / 2;
    var minDimension = originalWidth;
    if (originalHeight < minDimension) {
      minDimension = originalHeight;
    }
    var r = Math.pow(Math.pow(xLeg, 2) + Math.pow(yLeg, 2), 0.5) / (minDimension / 2);
    var rNew = stickAlign.aD * Math.pow(r, 4) + stickAlign.bD * Math.pow(r, 3) + stickAlign.cD * Math.pow(r, 2) + stickAlign.dD * r;
    var xDest = (rNew / r) * xLeg + originalWidth / 2;
    var yDest = (rNew / r) * yLeg + originalHeight / 2;

    x =
      (xDest + (leftChop - leftBorder) * (stickAlign.width / (originalWidth + leftBorder + rightBorder - leftChop - rightChop))) *
      (currentWidth / originalWidth);
    y = (yDest * currentHeight) / (originalHeight - topChop - bottomChop + topBorder + bottomBorder);
    return {
      x: x,
      y: y
    };
  },
  getFeet: function (x, y, stickAlign) {
    if (
      typeof stickAlign.aD === 'undefined' &&
      typeof stickAlign.bD === 'undefined' &&
      typeof stickAlign.cD === 'undefined' &&
      typeof stickAlign.dD === 'undefined'
    ) {
      stickAlign.aD = 0;
      stickAlign.bD = 0;
      stickAlign.cD = 0;
      stickAlign.dD = 1;
    } else if (stickAlign.aD == 0 && stickAlign.bD == 0 && stickAlign.cD == 0 && stickAlign.dD == 0) {
      stickAlign.dD = 1;
    }
    var topBorder = stickAlign.topBorder || 0;
    var bottomBorder = stickAlign.bottomBorder || 0;
    var leftBorder = stickAlign.leftBorder || 0;
    var rightBorder = stickAlign.rightBorder || 0;
    var topChop = stickAlign.topChop || 0;
    var bottomChop = stickAlign.bottomChop || 0;
    var leftChop = stickAlign.leftChop || 0;
    var rightChop = stickAlign.rightChop || 0;
    var originalWidth = stickAlign.originalWidth || stickAlign.width;
    var currentWidth = stickAlign.currentWidth || stickAlign.width;
    var originalHeight = stickAlign.originalHeight || stickAlign.height;
    var currentHeight = stickAlign.currentHeight || stickAlign.height;

    x = x * ((originalWidth - leftChop - rightChop + leftBorder + rightBorder) / currentWidth);
    y = y * ((originalHeight - topChop - bottomChop + topBorder + bottomBorder) / currentHeight);
    var xLeg = x - originalWidth / 2;
    var yLeg = y - originalHeight / 2;
    var minDimension = originalWidth;
    if (originalHeight < minDimension) {
      minDimension = originalHeight;
    }
    var r0 = Math.pow(Math.pow(xLeg, 2) + Math.pow(yLeg, 2), 0.5) / (minDimension / 2);
    var rSrc = r0;
    var rDiff = Number.MAX_VALUE;
    var rCalc = 0;
    var threshold = Math.pow(10, -10);
    while (rDiff > threshold) {
      rCalc =
        stickAlign.aD * Math.pow(rSrc, 4) + stickAlign.bD * Math.pow(rSrc, 3) + stickAlign.cD * Math.pow(rSrc, 2) + stickAlign.dD * rSrc;
      rDiff = r0 - rCalc;
      rSrc = rSrc + rDiff;
    }
    const correction = r0 === 0 ? 0 : rSrc / r0;
    x = correction * xLeg + originalWidth / 2;
    y = correction * yLeg + originalHeight / 2;
    return (stickAlign.A * y + stickAlign.B) / (stickAlign.C * y + 1);
  },
  getInches: function (x, y, stickAlign) {
    var feet = this.getFeet(x, y, stickAlign);
    return Round(feet * 12, 2);
  },
  getCameraConfigError: function (photo) {
    photo = photo || {};

    let width = photo.image_width;
    let height = photo.image_height;

    if (width > height) {
      let t = width;
      width = height;
      height = t;
    }

    let bestComboMatch = {
      cameraConfigLensBodySetup: {
        label: 'Camera/Lens Setup',
        value: true
      }
    };

    let camera_models = calibratePhoto.cameraModels || [];
    // Find the camera model that matches the given photo data, but don't require a matching image size
    let matchingBodyAndLenses = camera_models.filter(
      (x) => photo.camera_model.trim().toUpperCase() == x.name.toUpperCase() && (photo.lens == x.lens || x.lens == 'ALL')
    );
    // Found a matching camera, check to see if something is off
    if (matchingBodyAndLenses.length > 0) {
      matchingBodyAndLenses.forEach((combo) => {
        // If the size matches exactly, then set the size flag to true
        if (combo.height == height && combo.width == width) {
          bestComboMatch.cameraConfigSize = { label: 'Image Size', value: true };
        }
        // Otherwise if the size didn't match, only set the size flag to false
        // if we don't already have a size flag record
        else if (!bestComboMatch.cameraConfigSize) {
          bestComboMatch.cameraConfigSize = { label: 'Image Size', value: false };
        }
        // Get the photo's distortion control setting
        let distCorrEnabled = this.getDistortionControlSetting(photo);
        // Check if the photo setting matches the model
        if (
          (combo.distortion_correction === true && distCorrEnabled === true) ||
          (combo.distortion_correction === false && (distCorrEnabled === false || distCorrEnabled == null)) ||
          (combo.distortion_correction == null && distCorrEnabled == null)
        ) {
          bestComboMatch.cameraConfigDistCorr = { label: 'Distortion Correction', value: true };
        } else if (!bestComboMatch.cameraConfigDistCorr) {
          bestComboMatch.cameraConfigDistCorr = { label: 'Distortion Correction', value: false };
        }
      });
    } else {
      // If there were no matching body and lens combinations, then mark that setup as false
      bestComboMatch.cameraConfigLensBodySetup.value = false;
    }

    // no matching camera and lens combination
    return bestComboMatch;
  },
  getMatchingCameraModel: function (photo, options) {
    photo = photo || {};
    options = options || {};

    // requireSizeMatch: Requires that the image height and width match the camera model to pass check (defaults to true)
    if (options.requireSizeMatch == null) options.requireSizeMatch = true;

    // Get values for the camera model from the photo data
    let camera_model = photo.camera_model;
    let lens = photo.lens;
    let focal_length = photo.focal_length;
    let width = photo.image_width;
    let height = photo.image_height;
    if (camera_model != null) {
      if (width > height) {
        let t = width;
        width = height;
        height = t;
      }

      let camera_models = calibratePhoto.cameraModels || [];

      let matchingCombo = camera_models.find((x) => {
        let requirements = [];
        // Make sure camera model name matches.
        requirements.push(camera_model.trim().toUpperCase() == x.name.toUpperCase());
        // Make sure lens matches or is set to ALL.
        requirements.push(lens == x.lens || x.lens == 'ALL');
        // Check if we should make sure height and width match.
        if (options.requireSizeMatch == true) {
          requirements.push(x.height == height && x.width == width);
        }
        // Make sure focal length either matches or is not required.
        requirements.push(!Array.isArray(x.focalLengths) || x.focalLengths.includes(focal_length));

        for (let key in x.required_attributes) {
          requirements.push(photo[key] === x.required_attributes[key]);
        }

        // Make sure distortion correction is specified by neither or matches.
        let distCorrEnabled = this.getDistortionControlSetting(photo);
        // Make sure a camera that has distortion control on matches a model that will NOT flatten the image.
        // Make sure a camera that has distortion control off or unspecified matches a model that will flatten the distorted image.
        // Make sure a camera that isn't known to have distortion control (and suddenly appears with one) isn't matched with a model that doesn't specify distortion control (e.x. if a camera we assume has no distortion control setting has one, reject it so we can correct it).
        requirements.push(
          (x.distortion_correction === true && distCorrEnabled === true) ||
            (x.distortion_correction === false && (distCorrEnabled === false || distCorrEnabled == null)) ||
            (x.distortion_correction == null && distCorrEnabled == null)
        );
        return requirements.every((x) => x);
      });

      return matchingCombo || null;
    }

    return null;
  },
  getDistortionControlSetting: function (photo) {
    return DistortionCorrectionEnabled(photo);
  },
  fullAlign: function (originalValues, photoWidth, photoHeight, focalLength, cameraType, testCameraModel) {
    var distortionValues;
    if (cameraType == 'flat') {
      distortionValues = { bMin: 0, bMid: 0, bMax: 0 };
    } else if (cameraType == 'new' && testCameraModel != null) {
      var camera = testCameraModel;
      distortionValues = camera[focalLength];
    } else {
      var camera = this.cameraModelData[cameraType];
      if (typeof camera === 'undefined') {
        return { errorMessages: [{ type: 'UnknownCamera' }] };
      }
      distortionValues = camera[focalLength];
      if (typeof distortionValues === 'undefined') {
        return { errorMessages: [{ type: 'UnknownFocal' }] };
      }
    }
    var anchorCount = originalValues.length;
    originalValues.sort(function (a, b) {
      return b.y - a.y;
    });
    var bMidValues = [];
    var xSum = 0;
    for (var i = 0; i < originalValues.length; i++) {
      bMidValues.push({ x: originalValues[i].x, y: originalValues[i].y, height: originalValues[i].height });
      xSum += originalValues[i].x;
    }
    var bMidAlign = this.singleAlignWithErrors(bMidValues, photoWidth, photoHeight, '', '', distortionValues, 'Mid');
    var averageX = xSum / anchorCount;
    var pixelBuffer = ((1 - this.extrapolationCriteria.horizontalCenter) * photoWidth) / 2;

    if (averageX < pixelBuffer || averageX > photoWidth - pixelBuffer) {
      bMidAlign.errorMessages.push({ type: 'NotCentered', value: averageX });
    }
    bMidAlign.points.sort(function (a, b) {
      return b.y - a.y;
    });
    for (var i = 0; i < bMidAlign.points.length; i++) {
      originalValues[i].score = bMidAlign.points[i].score;
    }
    bMidAlign.points = originalValues;
    var heightMid = (originalValues[0].height + originalValues[anchorCount - 1].height) / 2;
    var ninetyFiveRatio = this.extrapolationCriteria.ninetyFiveRatio[anchorCount];
    if (typeof ninetyFiveRatio === 'undefined') {
      var maxRatio = 0;
      for (var key in this.extrapolationCriteria.ninetyFiveRatio) {
        if (key > maxRatio) {
          maxRatio = key;
          ninetyFiveRatio = this.extrapolationCriteria.ninetyFiveRatio[key];
        }
      }
    }
    var bMinShrinkValues = [];
    for (var i = 0; i < originalValues.length; i++) {
      if (i == 0) {
        bMinShrinkValues.push({
          x: originalValues[i].x,
          height: originalValues[i].height,
          y:
            originalValues[i].y +
            this.extrapolationCriteria.targetError *
              ((originalValues[i].y - originalValues[i + 1].y) / ((originalValues[i + 1].height - originalValues[i].height) * 12)) *
              ninetyFiveRatio
        });
      } else if (i == anchorCount - 1) {
        bMinShrinkValues.push({
          x: originalValues[i].x,
          height: originalValues[i].height,
          y:
            originalValues[i].y +
            this.extrapolationCriteria.targetError *
              ((originalValues[i - 1].y - originalValues[i].y) / ((originalValues[i].height - originalValues[i - 1].height) * 12)) *
              ninetyFiveRatio
        });
      } else {
        bMinShrinkValues.push({
          x: originalValues[i].x,
          height: originalValues[i].height,
          y:
            originalValues[i].y -
            this.extrapolationCriteria.targetError *
              ((originalValues[i - 1].y - originalValues[i + 1].y) / ((originalValues[i + 1].height - originalValues[i - 1].height) * 12)) *
              ninetyFiveRatio
        });
      }
    }
    var bMaxShrinkValues = [];
    for (var i = 0; i < bMinShrinkValues.length; i++) {
      bMaxShrinkValues.push({ x: bMinShrinkValues[i].x, y: bMinShrinkValues[i].y, height: bMinShrinkValues[i].height });
    }
    var bMinStretchValues = [];
    for (var i = 0; i < originalValues.length; i++) {
      if (i == 0) {
        bMinStretchValues.push({
          x: originalValues[i].x,
          height: originalValues[i].height,
          y:
            originalValues[i].y -
            this.extrapolationCriteria.targetError *
              ((originalValues[i].y - originalValues[i + 1].y) / ((originalValues[i + 1].height - originalValues[i].height) * 12)) *
              ninetyFiveRatio
        });
      } else if (i == anchorCount - 1) {
        bMinStretchValues.push({
          x: originalValues[i].x,
          height: originalValues[i].height,
          y:
            originalValues[i].y -
            this.extrapolationCriteria.targetError *
              ((originalValues[i - 1].y - originalValues[i].y) / ((originalValues[i].height - originalValues[i - 1].height) * 12)) *
              ninetyFiveRatio
        });
      } else {
        bMinStretchValues.push({
          x: originalValues[i].x,
          height: originalValues[i].height,
          y:
            originalValues[i].y +
            this.extrapolationCriteria.targetError *
              ((originalValues[i - 1].y - originalValues[i + 1].y) / ((originalValues[i + 1].height - originalValues[i - 1].height) * 12)) *
              ninetyFiveRatio
        });
      }
    }
    var bMaxStretchValues = [];
    for (var i = 0; i < bMinStretchValues.length; i++) {
      bMaxStretchValues.push({ x: bMinStretchValues[i].x, y: bMinStretchValues[i].y, height: bMinStretchValues[i].height });
    }
    var otherAligns = [];
    otherAligns.push(this.singleAlign(bMinShrinkValues, photoWidth, photoHeight, '', '', distortionValues, 'Min'));
    otherAligns.push(this.singleAlign(bMaxShrinkValues, photoWidth, photoHeight, '', '', distortionValues, 'Max'));
    otherAligns.push(this.singleAlign(bMinStretchValues, photoWidth, photoHeight, '', '', distortionValues, 'Min'));
    otherAligns.push(this.singleAlign(bMaxStretchValues, photoWidth, photoHeight, '', '', distortionValues, 'Max'));
    bMidAlign.otherAligns = otherAligns;
    return bMidAlign;
  },
  singleAlignWithErrors: function (values, photoWidth, photoHeight, focalLength, cameraModel, distortionValues, minMidMax) {
    // Initial Error Checking
    var errorMessages = [];
    var numOfValues = values.length;
    for (var i = 0; i < numOfValues; i++) {
      values[i].score = 1;
    }
    //Number of Anchors test
    if (numOfValues < this.criteria.numAncsReq) {
      for (var i = 0; i < numOfValues; i++) {
        values[i].score = 0;
      }
      errorMessages.push({ type: 'Number', value: numOfValues });
    }
    values.sort(function (a, b) {
      return b.height - a.height;
    });
    var largestUserHeight = values[0].height;
    //Stick Angle Test
    values.sort(function (a, b) {
      return a.x - b.x;
    });
    var minX = values[0].x;
    var maxX = values[values.length - 1].x;
    values.sort(function (a, b) {
      return b.y - a.y;
    });
    var minY = values[values.length - 1].y;
    var maxY = values[0].y;
    var stickAngle = (maxX - minX) / (maxY - minY);
    if (isNaN(stickAngle) || stickAngle > this.criteria.stickAngleReq) {
      for (var i = 0; i < numOfValues; i++) {
        values[i].score = 0;
      }
      errorMessages.push({ type: 'Angle', value: stickAngle });
    }

    //Stick Align
    var stickAlign = this.singleAlign(values, photoWidth, photoHeight, focalLength, cameraModel, distortionValues, minMidMax);

    // More Error Checking
    //Individual Anchor Test
    var capAtYellow = false;
    for (var i = 0; i < numOfValues; i++) {
      var anchorFit = Math.abs(values[i].height - this.getFeet(values[i].x, values[i].y, stickAlign)) / largestUserHeight;
      if (isNaN(anchorFit) || anchorFit > this.criteria.anchorFitReq) {
        capAtYellow = true;
        values[i].score = 0;
        errorMessages.push({
          type: 'FailedAnchor',
          value: {
            x: values[i].x,
            y: values[i].y,
            height: values[i].height,
            anchorFit: anchorFit,
            valueout: this.getFeet(values[i].x, values[i].y, stickAlign)
          }
        });
      } else {
        if (values[i].score != 0) {
          values[i].score = values[i].score - anchorFit;
        }
        errorMessages.push({ type: 'AnchorScore', value: { x: values[i].x, y: values[i].y, anchorFit: anchorFit } });
      }
    }
    //Normalized Global Anchor Test
    var normalizedError = 1 - Math.sqrt(stickAlign.error / numOfValues);
    if (normalizedError <= this.criteria.alignmentFitYwRdReq) {
      for (var i = 0; i < numOfValues; i++) {
        values[i].score = 0;
      }
      errorMessages.push({ type: 'FailedNormalized', value: normalizedError });
    }
    if (normalizedError >= this.criteria.alignmentFitGnYwReq) {
      errorMessages.push({ type: 'PassedNormalized', value: normalizedError });
    }
    if (normalizedError < this.criteria.alignmentFitGnYwReq && normalizedError > this.criteria.alignmentFitYwRdReq) {
      for (var i = 0; i < numOfValues; i++) {
        if (values[i].score != 0) {
          values[i].score = values[i].score - this.criteria.anchorFitReq;
        }
      }
      errorMessages.push({ type: 'PassedNormalized', value: normalizedError });
    }
    //Perspective and Parallax Test
    var anchorGapChangeRates = [];
    var meanAnchorGapChangeRate = 0;
    for (var i = 1; i < numOfValues; i++) {
      var rate =
        Math.abs(values[i].y - values[i - 1].y) / values[0].y / (Math.abs(values[i].height - values[i - 1].height) / largestUserHeight);
      meanAnchorGapChangeRate += rate;
      anchorGapChangeRates.push(rate);
    }
    meanAnchorGapChangeRate = meanAnchorGapChangeRate / anchorGapChangeRates.length;
    var sumForStdDev = 0;
    for (var i = 0; i < anchorGapChangeRates.length; i++) {
      sumForStdDev += (anchorGapChangeRates[i] - meanAnchorGapChangeRate) * (anchorGapChangeRates[i] - meanAnchorGapChangeRate);
    }
    var anchorGapStdDev = Math.sqrt(sumForStdDev / anchorGapChangeRates.length);
    if (anchorGapStdDev > this.criteria.anchorGapStdDevReq) {
      for (var i = 0; i < numOfValues; i++) {
        values[i].score = 0;
      }
    }
    if (capAtYellow) {
      for (var i = 0; i < numOfValues; i++) {
        if (values[i].score > this.criteria.anchorFlagGnYwReq) {
          values[i].score = this.criteria.anchorFlagGnYwReq;
        }
      }
    }
    errorMessages.push({ type: 'Perspective', value: anchorGapStdDev });

    //Add final values
    stickAlign.points = values;
    stickAlign.error = normalizedError;
    stickAlign.errorMessages = errorMessages;
    return stickAlign;
  },
  singleAlign: function (values, photoWidth, photoHeight, focalLength, cameraModel, distortionValues, minMidMax) {
    // Get X and Y average and highest point before distortionControl for stick line
    values = JSON.parse(JSON.stringify(values));
    var xAverage = 0;
    var yAverage = 0;
    var stickBottomX = Infinity;
    var stickBottomY = Infinity;
    var numOfValues = values.length;
    for (var i = 0; i < numOfValues; i++) {
      xAverage += values[i].x;
      yAverage += values[i].y;
      if (values[i].y < stickBottomY) {
        stickBottomX = values[i].x;
        stickBottomY = values[i].y;
      }
    }
    xAverage = xAverage / numOfValues;
    yAverage = yAverage / numOfValues;

    //Stick Line Least Squares Fit X = a + b * Y (swapped x and y for near vertical line fit.)
    var numerator = 0;
    var denominator = 0;
    for (var i = 0; i < values.length; i++) {
      numerator += (values[i].x - xAverage) * (values[i].y - yAverage);
      denominator += Math.pow(values[i].y - yAverage, 2);
    }
    var b = numerator / denominator;
    var a = xAverage - b * yAverage;

    var stickTopX = a;

    // Get Distortion Control
    var distortionControl = this.getDistortionControl(
      values,
      photoWidth,
      photoHeight,
      cameraModel,
      focalLength,
      distortionValues,
      minMidMax
    );
    var values = distortionControl.points;
    // Run Stick Align
    var mA = new Array();
    var mW = new Array();

    for (var i = 0; i < numOfValues; i++) {
      for (var j = 0; j < numOfValues; j++) {
        if (values[i].height == values[j].height && values[i].y != values[j].y && i != j) {
          var stickAlign = {
            error: -1,
            errorMessages: ['Spacing', 'Two anchors with same height']
          };
          return stickAlign;
        }
      }
      var thisMa = new Array();
      thisMa[0] = values[i].y;
      thisMa[1] = 1;
      thisMa[2] = values[i].y * values[i].height * -1;
      mA[i] = thisMa;
      mW[i] = values[i].height;
    }

    var matrixA = Matrix.create(mA);
    var matrixW = Matrix.create(mW);
    var matrixAt = matrixA.transpose();
    var matrixAtA = matrixAt.x(matrixA);
    var matrixAtAi = matrixAtA.inverse();
    var matrixAtAiAt = matrixAtAi.x(matrixAt);
    var matrixX = matrixAtAiAt.x(matrixW);
    var mV = matrixA.x(matrixX).subtract(matrixW);

    var error = 0;
    for (var j = 0; j < mV.cols(); j++) {
      error += mV.e(j + 1, 1) * mV.e(j + 1, 1);
    }

    var stickAlign = {
      A: matrixX.e(1, 1),
      B: matrixX.e(2, 1),
      C: matrixX.e(3, 1),
      stickBottomX: stickBottomX,
      stickBottomY: stickBottomY,
      stickTopX: stickTopX,
      stickTopY: 0,
      aD: distortionControl.aD,
      bD: distortionControl.bD,
      cD: distortionControl.cD,
      dD: distortionControl.dD,
      height: photoHeight,
      width: photoWidth,
      error: error
    };
    return stickAlign;
  },
  getDistortionControl: function (points, photoWidth, photoHeight, cameraType, focalLength, distortionValues, minMidMax) {
    var a = distortionValues['a' + minMidMax] || 0;
    var b = distortionValues['b' + minMidMax] || 0;
    var c = distortionValues['c' + minMidMax] || 0;
    var d = 1 - a - b - c;
    for (var i = 0; i < points.length; i++) {
      var xLeg = points[i].x - photoWidth / 2;
      var yLeg = points[i].y - photoHeight / 2;
      var minDimension = photoWidth;
      if (photoHeight < minDimension) {
        minDimension = photoHeight;
      }
      var r0 = Math.pow(Math.pow(xLeg, 2) + Math.pow(yLeg, 2), 0.5) / (minDimension / 2);
      var rSrc = r0;
      var rDiff = 1.7976931348623157e308;
      var rCalc = 0;
      var threshold = Math.pow(10, -10);
      while (rDiff > threshold) {
        rCalc = a * Math.pow(rSrc, 4) + b * Math.pow(rSrc, 3) + c * Math.pow(rSrc, 2) + d * rSrc;
        rDiff = r0 - rCalc;
        rSrc = rSrc + rDiff;
      }
      points[i].x = (rSrc / r0) * xLeg + photoWidth / 2;
      points[i].y = (rSrc / r0) * yLeg + photoHeight / 2;
    }
    return { points: points, aD: a, bD: b, cD: c, dD: d };
  }
};
// window.addEventListener('load', calibratePhoto.ready.bind(calibratePhoto));
calibratePhoto.ready();

export default calibratePhoto;
